import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { Clock, ChefHat, MapPin, Star } from "lucide-react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";
import { useDict } from "@/i18n/dict";
import ReviewsSection from "@/components/ReviewsSection";
import { RECIPE } from "@/constants/testIds";

export default function RecipeDetailPage() {
  const { slug } = useParams();
  const { lang } = useLang();
  const t = useDict(lang);
  const [r, setR] = useState(null);
  const [err, setErr] = useState(false);
  const [tab, setTab] = useState("ingredients");

  useEffect(() => {
    api.getRecipe(slug).then(setR).catch(() => setErr(true));
  }, [slug]);

  // SEO: dynamic <title>, <meta>, JSON-LD Recipe schema (https://schema.org/Recipe)
  useEffect(() => {
    if (!r) return undefined;
    const title = r.title[lang] || r.title.fr;
    const summary = r.summary?.[lang] || r.summary?.fr || "";
    const ings = r.ingredients?.[lang] || r.ingredients?.fr || [];
    const steps = r.steps?.[lang] || r.steps?.fr || [];

    const prevTitle = document.title;
    document.title = `${title} — Recette ${r.country} · BAYO`;
    const setMeta = (sel, val) => {
      let el = document.head.querySelector(sel);
      if (!el) {
        el = document.createElement("meta");
        const isOg = sel.includes("property=");
        const attr = isOg ? "property" : "name";
        const m = sel.match(/\[(?:name|property)="([^"]+)"\]/);
        if (m) el.setAttribute(attr, m[1]);
        document.head.appendChild(el);
      }
      el.setAttribute("content", val);
    };
    setMeta('meta[name="description"]', summary.slice(0, 200));
    setMeta('meta[property="og:title"]', `${title} — Recette BAYO`);
    setMeta('meta[property="og:description"]', summary.slice(0, 200));
    setMeta('meta[property="og:image"]', r.image);
    setMeta('meta[property="og:type"]', "article");
    setMeta('meta[name="twitter:title"]', `${title} — Recette BAYO`);
    setMeta('meta[name="twitter:description"]', summary.slice(0, 200));
    setMeta('meta[name="twitter:image"]', r.image);

    const jsonLd = {
      "@context": "https://schema.org",
      "@type": "Recipe",
      "name": title,
      "description": summary,
      "image": [r.image],
      "recipeCuisine": r.country,
      "recipeCategory": r.type,
      "totalTime": `PT${r.time_minutes}M`,
      "recipeYield": "4 portions",
      "recipeIngredient": ings,
      "recipeInstructions": steps.map((text, idx) => ({
        "@type": "HowToStep",
        "position": idx + 1,
        "text": text,
      })),
      "aggregateRating": r.reviews_count > 0 ? {
        "@type": "AggregateRating",
        "ratingValue": r.rating,
        "reviewCount": r.reviews_count,
      } : undefined,
      "author": { "@type": "Organization", "name": "BAYO" },
    };
    let ld = document.head.querySelector('script[type="application/ld+json"][data-bayo-recipe]');
    if (!ld) {
      ld = document.createElement("script");
      ld.setAttribute("type", "application/ld+json");
      ld.setAttribute("data-bayo-recipe", "1");
      document.head.appendChild(ld);
    }
    ld.textContent = JSON.stringify(jsonLd);
    return () => {
      document.title = prevTitle;
      const el = document.head.querySelector('script[type="application/ld+json"][data-bayo-recipe]');
      if (el) el.remove();
    };
  }, [r, lang]);

  if (err) return <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-32 text-center font-serif-display text-3xl italic">Recipe not found</div>;
  if (!r) return <div className="max-w-[1400px] mx-auto px-6 lg:px-12 py-32" />;

  const title = r.title[lang] || r.title.fr;
  const summary = r.summary?.[lang] || r.summary?.fr || "";
  const ings = r.ingredients?.[lang] || r.ingredients?.fr || [];
  const steps = r.steps?.[lang] || r.steps?.fr || [];

  return (
    <div className="max-w-[1400px] mx-auto px-6 lg:px-12 pt-12">
      <Link to="/recipes" data-testid={RECIPE.back} className="text-xs tracking-[0.22em] uppercase font-medium hover:opacity-60">{t.recipe.backToAll}</Link>

      <div className="grid lg:grid-cols-12 gap-12 mt-10">
        <div className="lg:col-span-7">
          <div className="aspect-[4/5] overflow-hidden bg-[var(--bg-2)]">
            <img src={r.image} alt={title} className="w-full h-full object-cover" />
          </div>
        </div>
        <div className="lg:col-span-5 lg:pt-8">
          <p className="eyebrow mb-4">{r.country} · {t.types[r.type] || r.type}</p>
          <h1 data-testid={RECIPE.title} className="font-serif-display text-5xl lg:text-6xl leading-[1.02] mb-6">{title}</h1>
          <div className="flex items-center gap-3 text-sm mb-6">
            <span className="inline-flex items-center gap-0.5 text-[color:var(--mango)]">
              {[1,2,3,4,5].map((i) => (
                <Star key={i} size={14} fill={i <= Math.round(r.rating || 4.7) ? 'currentColor' : 'none'} strokeWidth={1.5} />
              ))}
            </span>
            <span className="font-medium">{(r.rating || 4.7).toFixed(1)}</span>
            <span className="text-[color:var(--ink-3)]">· {r.reviews_count || 0} {lang === 'fr' ? 'avis' : 'reviews'}</span>
          </div>
          <p className="text-base text-[color:var(--ink-2)] leading-relaxed mb-8">{summary}</p>

          <div className="grid grid-cols-3 border-t border-b border-[color:rgba(0,16,48,0.15)] divide-x divide-[color:rgba(0,16,48,0.15)]">
            <div className="py-4 pr-4">
              <p className="eyebrow mb-1"><MapPin size={12} className="inline" /> {t.recipe.country}</p>
              <p className="font-serif-display text-xl">{r.country}</p>
            </div>
            <div className="py-4 px-4">
              <p className="eyebrow mb-1"><Clock size={12} className="inline" /> {t.recipe.time}</p>
              <p className="font-serif-display text-xl">{r.time_minutes} min</p>
            </div>
            <div className="py-4 pl-4">
              <p className="eyebrow mb-1"><ChefHat size={12} className="inline" /> {t.recipe.difficulty}</p>
              <p className="font-serif-display text-xl">{t.difficulties[r.difficulty]}</p>
            </div>
          </div>
          <p className="mt-6 text-sm text-[color:var(--ink-3)]">{t.recipe.by} <span className="font-medium text-[color:var(--ink)]">{r.author_name}</span></p>
        </div>
      </div>

      {/* Tabs */}
      <div className="mt-20 grid lg:grid-cols-12 gap-12">
        <div className="lg:col-span-4">
          <div className="flex gap-6 mb-8 border-b border-[color:rgba(0,16,48,0.15)] pb-4">
            <button data-testid={RECIPE.ingredientsTab} onClick={() => setTab("ingredients")} className={`text-xs tracking-[0.22em] uppercase font-medium ${tab==='ingredients'?'text-[color:var(--red)] underline underline-offset-8':''}`}>{t.recipe.ingredients}</button>
            <button data-testid={RECIPE.stepsTab} onClick={() => setTab("steps")} className={`text-xs tracking-[0.22em] uppercase font-medium ${tab==='steps'?'text-[color:var(--red)] underline underline-offset-8':''}`}>{t.recipe.steps}</button>
          </div>
        </div>

        <div className="lg:col-span-8">
          {tab === "ingredients" ? (
            <ul className="space-y-3">
              {ings.map((ing, i) => (
                <li key={`ing-${i}-${ing.slice(0, 16)}`} className="flex gap-4 items-baseline pb-3 border-b border-[color:rgba(0,16,48,0.08)]">
                  <span className="font-serif-display text-[color:var(--red)] text-lg w-8">{String(i+1).padStart(2, "0")}</span>
                  <span className="text-base">{ing}</span>
                </li>
              ))}
            </ul>
          ) : (
            <ol className="space-y-6">
              {steps.map((s, i) => {
                // Interleave step images: show first image after step 1, second image roughly in the middle
                const stepImages = r.step_images || [];
                const showImg = stepImages.length > 0 && (
                  (i === 0 && stepImages[0]) ||
                  (i === Math.floor(steps.length / 2) && stepImages[1])
                );
                const imgUrl = i === 0 ? stepImages[0] : (i === Math.floor(steps.length / 2) ? stepImages[1] : null);
                return (
                  <div key={`step-${i}-${s.slice(0, 16)}`}>
                    <li className="grid grid-cols-[60px_1fr] gap-4">
                      <span className="font-serif-display text-[color:var(--navy)] text-4xl leading-none italic">{i+1}.</span>
                      <p className="text-base leading-relaxed text-[color:var(--ink)]">{s}</p>
                    </li>
                    {showImg && imgUrl && (
                      <div className="mt-6 mb-2 ml-[76px] overflow-hidden bg-[var(--bg-2)] max-w-2xl">
                        <img src={imgUrl} alt={`Étape ${i+1}`} className="w-full aspect-[16/10] object-cover" loading="lazy" />
                      </div>
                    )}
                  </div>
                );
              })}
            </ol>
          )}
        </div>
      </div>

      {/* Process gallery if step images available */}
      {r.step_images && r.step_images.length > 0 && (
        <section className="mt-24">
          <div className="flex items-baseline gap-4 mb-8">
            <span className="section-num">03</span>
            <h2 className="eyebrow">{lang === 'fr' ? 'En cuisine' : 'In the kitchen'}</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {r.step_images.map((img, i) => (
              <div key={`step-img-${img}`} className="aspect-[4/3] overflow-hidden bg-[var(--bg-2)]">
                <img src={img} alt="" className="w-full h-full object-cover" loading="lazy" />
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Tools & spices shop CTA */}
      {r.tools && r.tools.length > 0 && (
        <section data-testid="recipe-tools" className="mt-24 bg-[var(--bg-2)] -mx-6 lg:-mx-12 px-6 lg:px-12 py-16">
          <div className="flex items-baseline gap-4 mb-8">
            <span className="section-num">{r.step_images?.length > 0 ? '04' : '03'}</span>
            <h2 className="eyebrow">{t.tools.title}</h2>
          </div>
          <p className="text-base text-[color:var(--ink-2)] max-w-2xl mb-10">{t.tools.sub}</p>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {r.tools.map((p) => (
              <Link
                key={p.product_id}
                to={`/shop`}
                data-testid={`recipe-tool-${p.slug}`}
                className="group block bg-[var(--bg)]"
              >
                <div className="aspect-square overflow-hidden bg-[var(--bg-2)]">
                  <img src={p.image} alt={p.name[lang] || p.name.fr} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="p-4">
                  <h3 className="font-serif-display text-lg leading-tight group-hover:text-[color:var(--navy)] transition-colors">{p.name[lang] || p.name.fr}</h3>
                  <div className="mt-2 flex items-center justify-between">
                    <span className="font-serif-display text-xl">{p.price.toFixed(2)} €</span>
                    <span className="text-xs tracking-[0.2em] uppercase underline">{lang === 'fr' ? 'Voir' : 'View'}</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>
      )}

      <ReviewsSection slug={slug} />
    </div>
  );
}
